import React, {forwardRef, useCallback, useEffect, useState} from 'react'
import Konva from 'konva'
import {useNavigate, useParams} from 'react-router-dom'
import {deleteDesignLibraryTemplate} from '../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../shared/PFToast'
import {useSelectedVariantContext} from '../../context/SelectedVariantContext'
import {FILTER_VALUE} from '../../../../constant/shapes'
import {BASIC_SHAPES, CUTOUT_SHAPES} from './constants'
import {useDraftShapes} from '../../../custom-shape/ui/hooks/useDraftShapes'
import {usePagination} from '../../../custom-shape/ui/hooks/usePagination'
import {useShapesTemplateList} from '../../../custom-shape/ui/hooks/useShapesTemplateList'
import {hasFeatureTag} from '../../../../helpers/settings'
import DeleteShapeConfirmModal from '../../../custom-shape/ui/components/sidebar/shapes/DeleteShapeConfirmModal'
import SavedShapesSection from '../../../custom-shape/ui/components/sidebar/shapes/SavedShapesSection'
import DraftShapesSection from '../../../custom-shape/ui/components/sidebar/shapes/DraftShapesSection'
import BasicShapesSection from '../../../custom-shape/ui/components/sidebar/shapes/BasicShapesSection'
import useEditorSafeZone from '../../../../hooks/useEditorSafeZone'
import {calcScale} from '../../../../helpers/shapes/calcScale'
import {validateSafeZoneImage} from '../../../../helpers/shapes/validateSafeZoneImage'
import {SHAPE_PATHS} from '../../../../helpers/shapes/shapePaths'

const CUSTOM_SHAPE_EDIT_PAYLOAD_KEY = 'customShapeEditPayload'
const BASIC_SHAPE_LIMIT = 9
const EMPTY_STATE = <p className="NoData SidebarPanel__Empty">No items yet</p>

const buildBasicShapeItem = ({type, name}) => {
    const def = SHAPE_PATHS[type]
    if (!def) return null

    return {
        shapeType: type,
        data: def.data,
        rawWidth: def.rawWidth,
        rawHeight: def.rawHeight,
        label: name,
    }
}

const loadImageElement = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('Failed to load shape preview'))
        image.src = src
    })

const createShapeSvgMarkup = ({data, rawWidth, rawHeight}) =>
    `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${rawWidth} ${rawHeight}" width="${rawWidth}" height="${rawHeight}">
        <path d="${data}" fill="#303238" />
    </svg>
`.trim()

const createShapeSvgDataUrl = ({data, rawWidth, rawHeight}) => {
    const svg = createShapeSvgMarkup({data, rawWidth, rawHeight})
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

const createDataUrlFromImage = (
    image,
    width = image.naturalWidth || image.width,
    height = image.naturalHeight || image.height,
) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')

    if (!context) {
        throw new Error('Failed to create shape preview')
    }

    context.clearRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    return canvas.toDataURL('image/png')
}

const createTemplateSafeZone = async (src, background) => {
    const image = await loadImageElement(src)
    const width = image.naturalWidth || image.width
    const height = image.naturalHeight || image.height
    const size = Math.min(background.width || 1000, background.height || 1000)
    const scale = calcScale({width, height}, size, true)

    return {
        src: createDataUrlFromImage(image),
        width,
        height,
        ...scale,
    }
}

const createBasicShapeSafeZone = async (shape, background) => {
    const size = Math.min(background.width || 1000, background.height || 1000)
    const scale = calcScale({width: shape.rawWidth, height: shape.rawHeight}, size, true)
    const scaledWidth = Math.max(1, Math.round(shape.rawWidth * scale.scaleX))
    const scaledHeight = Math.max(1, Math.round(shape.rawHeight * scale.scaleY))
    const tempStage = new Konva.Stage({
        container: document.createElement('div'),
        width: scaledWidth,
        height: scaledHeight,
    })

    try {
        const tempLayer = new Konva.Layer()
        tempStage.add(tempLayer)

        const tempPath = new Konva.Path({
            data: shape.data,
            fill: '#303238',
            stroke: '#303238',
            strokeWidth: 0,
            x: scaledWidth / 2,
            y: scaledHeight / 2,
            offsetX: shape.rawWidth / 2,
            offsetY: shape.rawHeight / 2,
            scaleX: scale.scaleX,
            scaleY: scale.scaleY,
        })

        tempLayer.add(tempPath)
        tempLayer.batchDraw()

        const src = tempPath.toDataURL({
            pixelRatio: 1,
            mimeType: 'image/png',
        })

        return {
            src,
            width: scaledWidth,
            height: scaledHeight,
            scaleX: 1,
            scaleY: 1,
        }
    } finally {
        tempStage.destroy()
    }
}

/**
 * List of shapes component
 * @param {Object} props - Component props
 * @param {string} props.typeFilter - Filter for shapes ('all', 'basic', 'custom')
 * @param {string} props.nameFilter - Filter for shapes name
 * @param {string} [props.productId] - Product id for callback URL when navigating to edit
 * @returns {JSX.Element} - List of shapes component
 */
export const ModalShapesContent = forwardRef(({typeFilter, nameFilter, productId: productIdProp}, ref) => {
    const {
        state: {triggerLoadTemplate},
    } = useSelectedVariantContext()
    const navigate = useNavigate()
    const {productId: productIdFromParams} = useParams()
    const productId = productIdProp ?? productIdFromParams
    const {drafts, loading: draftsLoading, deleteDraft} = useDraftShapes()
    const [deleteConfirm, setDeleteConfirm] = useState(null)
    const {background, setSafeZone} = useEditorSafeZone()

    const customShapeStep2Enabled = hasFeatureTag('custom_shape_step2')
    const {
        items: listTemplateShapes,
        setItems: setListTemplateShapes,
        limit: savedShapesLimit,
        loading,
        error: savedShapesError,
        pagination: savedPagination,
        setFilters,
    } = useShapesTemplateList({
        limit: 9,
        enabled: customShapeStep2Enabled,
        filters: {name: nameFilter},
        refreshKey: triggerLoadTemplate,
    })

    useEffect(() => {
        if (!customShapeStep2Enabled) return
        setFilters({name: nameFilter})
    }, [customShapeStep2Enabled, nameFilter, setFilters])

    useEffect(() => {
        if (!savedShapesError) return
        toaster({
            type: 'error',
            message: savedShapesError.message || 'Failed to load shapes',
        })
    }, [savedShapesError])

    useEffect(() => {
        const container = document.getElementById('ModalShapesContent')
        if (container) {
            container.scrollTo({top: 0})
        }
    }, [typeFilter])

    const listBasicShape = BASIC_SHAPES.concat(CUTOUT_SHAPES).map(buildBasicShapeItem).filter(Boolean)

    const isDisplayBasic = [FILTER_VALUE.BASIC, FILTER_VALUE.ALL].includes(typeFilter)
    const isDisplayTemplate = [FILTER_VALUE.TEMPLATE, FILTER_VALUE.ALL].includes(typeFilter)
    const listDraftShapes = drafts.filter((draft) => draft.previewUrl)

    const basicPagination = usePagination({
        totalItems: listBasicShape.length,
        limit: BASIC_SHAPE_LIMIT,
        resetKey: `${typeFilter}-${nameFilter}-${listBasicShape.length}`,
    })

    const draftPagination = usePagination({
        totalItems: listDraftShapes.length,
        limit: 9,
        resetKey: `${typeFilter}-${nameFilter}-${listDraftShapes.length}`,
    })

    const pagedBasicShapes = listBasicShape.slice(basicPagination.startIndex, basicPagination.endIndex)
    const pagedDraftShapes = listDraftShapes.slice(draftPagination.startIndex, draftPagination.endIndex)

    const applySafeZonePreview = useCallback(
        async (nextSafeZone) => {
            if (!background || !nextSafeZone?.src) return

            try {
                await validateSafeZoneImage(nextSafeZone.src)
                await setSafeZone(nextSafeZone)
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message || 'Safe zone image is invalid.',
                })
            }
        },
        [background, setSafeZone],
    )

    const handleSelectTemplate = useCallback(
        async (template) => {
            if (!template?.preview_image) return

            try {
                const nextSafeZone = await createTemplateSafeZone(template.preview_image, background)
                await applySafeZonePreview(nextSafeZone)
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message || 'Failed to load shape preview',
                })
            }
        },
        [applySafeZonePreview],
    )

    const handleSelectDraft = useCallback(
        async (draft) => {
            if (!draft?.previewUrl) return

            try {
                const nextSafeZone = await createTemplateSafeZone(draft.previewUrl, background)
                await applySafeZonePreview(nextSafeZone)
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message || 'Failed to load shape preview',
                })
            }
        },
        [applySafeZonePreview],
    )

    const handleSelectBasicShape = useCallback(
        async (shapeType) => {
            const shape = listBasicShape.find((item) => item.shapeType === shapeType)
            if (!shape) return

            try {
                const nextSafeZone = await createBasicShapeSafeZone(shape, background)
                await applySafeZonePreview(nextSafeZone)
            } catch (error) {
                toaster({
                    type: 'error',
                    message: error.message || 'Failed to load shape preview',
                })
            }
        },
        [applySafeZonePreview, background, listBasicShape],
    )

    const handleEditTemplate = useCallback(
        (template) => {
            const payload = {
                source: 'template',
                templateId: template._id,
                doc: template.data,
                meta: {
                    id: template._id,
                    name: template.name,
                    description: template.description,
                },
            }
            try {
                window.localStorage.setItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY, JSON.stringify(payload))
            } catch {
                toaster({type: 'error', message: 'Failed to prepare edit'})
                return
            }
            const callbackPath = productId ? productId : ''
            navigate(`/create-custom-shape?callbackUrl=${callbackPath}&edit=1`)
        },
        [navigate, productId],
    )

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteConfirm) return

        if (deleteConfirm.type === 'draft') {
            deleteDraft(deleteConfirm.item.id)
        }

        if (deleteConfirm.type === 'saved') {
            const {success, message} = await deleteDesignLibraryTemplate(deleteConfirm.item._id)
            if (!success) {
                toaster({type: 'error', message: message || 'Failed to delete shape'})
                return
            }
            toaster({type: 'success', message: 'Shape deleted successfully'})
            setListTemplateShapes((prev) => prev.filter((item) => item._id !== deleteConfirm.item._id))
        }

        setDeleteConfirm(null)
    }, [deleteConfirm, deleteDraft])

    const closeDeleteModal = useCallback(() => {
        setDeleteConfirm(null)
    }, [])

    const handleEditDraft = useCallback(
        (draft) => {
            const payload = {source: 'draft', draftId: draft.id}
            try {
                window.localStorage.setItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY, JSON.stringify(payload))
            } catch {
                toaster({type: 'error', message: 'Failed to prepare edit'})
                return
            }
            const callbackPath = productId ? productId : ''
            navigate(`/create-custom-shape?callbackUrl=${callbackPath}&edit=1`)
        },
        [navigate, productId],
    )

    return (
        <div className="ModalShapesContent" id="ModalShapesContent" ref={ref}>
            <div>
                {isDisplayTemplate && (
                    <SavedShapesSection
                        title="Saved shapes"
                        items={listTemplateShapes}
                        loading={loading}
                        error={savedShapesError}
                        skeletonCount={savedShapesLimit}
                        emptyComponent={EMPTY_STATE}
                        pagination={savedPagination}
                        onSelectShape={handleSelectTemplate}
                        onEditShape={handleEditTemplate}
                        onDeleteShape={(template) => setDeleteConfirm({type: 'saved', item: template})}
                    />
                )}

                {isDisplayBasic && (
                    <BasicShapesSection
                        title="Basic shapes"
                        items={pagedBasicShapes}
                        pagination={basicPagination}
                        emptyComponent={EMPTY_STATE}
                        onAddShape={handleSelectBasicShape}
                    />
                )}

                {isDisplayTemplate && (
                    <DraftShapesSection
                        title="Draft shapes"
                        items={pagedDraftShapes}
                        loading={draftsLoading}
                        skeletonCount={9}
                        emptyComponent={EMPTY_STATE}
                        pagination={draftPagination}
                        onSelectDraft={handleSelectDraft}
                        onEditDraft={handleEditDraft}
                        onDeleteDraft={(draft) => setDeleteConfirm({type: 'draft', item: draft})}
                    />
                )}
            </div>

            {deleteConfirm && (
                <DeleteShapeConfirmModal
                    isOpen={!!deleteConfirm}
                    toggle={closeDeleteModal}
                    type={deleteConfirm.type}
                    itemName={deleteConfirm.type === 'saved' ? deleteConfirm.item.name : undefined}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </div>
    )
})

export default ModalShapesContent
